import { useCallback, useEffect, useMemo, useRef, useState, type ReactNode } from "react";
import { useLocation } from "react-router-dom";
import { useQueryClient } from "@tanstack/react-query";
import { toast } from "@/components/ui/sonner";
import { StudentToofiContext } from "@/context/student-toofi-context";
import { useAiPlanFeatures } from "@/hooks/use-ai-plan-features";
import { useAuth } from "@/hooks/useAuth";
import { useFeatureFlag } from "@/hooks/useFeatureFlag";
import { getStoredExplainLanguage } from "@/lib/ai-explain-client";
import { requestAiAction } from "@/lib/ai-action-client";
import { requestReaderAiChat, type ReaderAiChatRequest } from "@/lib/reader-ai-chat-client";
import { requestSaveStudyPlan } from "@/lib/study-plan-client";
import {
  appendMessageToThread,
  createAssistantMessage,
  createEmptyThreadSummary,
  createSystemMessage,
  createUserMessage,
  deriveReaderAiThreadTitle,
  isAutoReaderAiThreadTitle,
  type ReaderAiPersistedState,
} from "@/lib/reader-ai-threads";
import { readerAiMessageToText, summarizeThreadMessages } from "@/lib/reader-ai-thread-summary";
import { buildSaveStudyPlanPayloadFromMessage, fetchActiveStudyPlan, saveStudyPlanCheckin, studyPlanQueryKeys } from "@/lib/study-plan";
import { buildStudentToofiSurfaceContext } from "@/lib/student-toofi-context";
import { normalizeStudentToofiOpenIntent } from "@/lib/student-toofi-open-intent";
import {
  createStudentToofiThread,
  readStudentToofiActiveThreadId,
  readStudentToofiComposerDraft,
  readStudentToofiState,
  writeStudentToofiActiveThreadId,
  writeStudentToofiComposerDraft,
  writeStudentToofiState,
} from "@/lib/student-toofi-threads";
import { captureToofiAnalytics } from "@/lib/toofi-analytics";
import { inferToofiProductIntent } from "@/lib/toofi-mode-intents";
import { READER_TOOFI_VISIBILITY_EVENT } from "@/lib/toofi-bridge";
import { coerceThreadSummary } from "@/lib/toofi/thread-summary-contract";
import type { ReaderAiBlock, ReaderAiMessage, ToofiMemoryMode, ToofiProductIntent, ToofiProductMode } from "@/types/agentic-chat";
import type {
  GuidedLessonControlInput,
  GuidedLessonSubmitInput,
  StudentToofiOpenIntent,
  StudentToofiProviderValue,
  StudentToofiSurfaceContext,
  StudentToofiSurfaceRegistration,
} from "@/types/student-toofi";

const detectPromptLanguage = (value: string) => (/[\u0600-\u06ff]/.test(value) ? "ar" : "en") as "ar" | "en";
const isQuotaPauseMessage = (value: string) => {
  const normalized = value.toLowerCase();
  return (
    normalized.includes("quota_limit_reached")
    || normalized.includes("toofi is paused")
    || normalized.includes("temporarily paused")
    || (normalized.includes("limit") && normalized.includes("toofi"))
  );
};

const supportedSurfaces = new Set([
  "dashboard",
  "library",
  "resource_detail",
  "profile",
  "review",
  "study_time",
  "inbox",
  "my_library",
  "my_passes",
  "pass_detail",
  "my_orders",
  "offline_library",
  "ai_plan",
]);

const normalizeThreadState = (state: ReaderAiPersistedState): ReaderAiPersistedState => ({
  ...state,
  threads: [...state.threads].sort((a, b) => {
    if (Boolean(a.pinned) !== Boolean(b.pinned)) return a.pinned ? -1 : 1;
    return b.updatedAt - a.updatedAt;
  }),
});

const updateThreadMessages = (
  state: ReaderAiPersistedState,
  threadId: string,
  updater: (messages: ReaderAiMessage[]) => ReaderAiMessage[],
): ReaderAiPersistedState => {
  const nextMessages = updater(state.messagesByThread[threadId] ?? []);
  const updatedAt = Date.now();
  return {
    ...state,
    threads: state.threads.map((thread) =>
      thread.id === threadId
        ? {
            ...thread,
            updatedAt,
            messageCount: nextMessages.length,
          }
        : thread,
    ),
    messagesByThread: {
      ...state.messagesByThread,
      [threadId]: nextMessages,
    },
    savedAt: updatedAt,
  };
};

export function StudentToofiProvider({ children }: { children: ReactNode }) {
  const location = useLocation();
  const queryClient = useQueryClient();
  const { profile, session, isLoading: authLoading } = useAuth();
  const rolloutEnabled = useFeatureFlag("ai_explain");
  const { hasFeature } = useAiPlanFeatures();
  const available = !authLoading && Boolean(session) && rolloutEnabled && hasFeature("ai_explain");
  const [surfaceRegistration, setSurfaceRegistration] = useState<StudentToofiSurfaceRegistration | null>(null);
  const [persistedState, setPersistedState] = useState<ReaderAiPersistedState>(() => readStudentToofiState());
  const [activeThreadId, setActiveThreadId] = useState<string | null>(() => readStudentToofiActiveThreadId());
  const [promptDraft, setPromptDraftState] = useState(() => readStudentToofiComposerDraft());
  const [open, setOpen] = useState(false);
  const [readerSurfaceOpen, setReaderSurfaceOpen] = useState(false);
  const [pendingIntent, setPendingIntent] = useState<StudentToofiOpenIntent | null>(null);
  const pendingIntentRef = useRef<StudentToofiOpenIntent | null>(null);
  const persistedStateRef = useRef(persistedState);
  const activeThread = persistedState.threads.find((thread) => thread.id === activeThreadId) ?? null;
  const messages = useMemo(
    () => (activeThreadId ? persistedState.messagesByThread[activeThreadId] ?? [] : []),
    [activeThreadId, persistedState.messagesByThread],
  );

  const surfaceContext = useMemo(
    () => buildStudentToofiSurfaceContext({ pathname: location.pathname, registration: surfaceRegistration }),
    [location.pathname, surfaceRegistration],
  );
  const surfaceSupported = supportedSurfaces.has(surfaceContext.surface);

  useEffect(() => {
    pendingIntentRef.current = pendingIntent;
  }, [pendingIntent]);

  useEffect(() => {
    persistedStateRef.current = persistedState;
  }, [persistedState]);

  useEffect(() => {
    writeStudentToofiState(persistedState);
  }, [persistedState]);

  useEffect(() => {
    writeStudentToofiActiveThreadId(activeThreadId);
  }, [activeThreadId]);

  useEffect(() => {
    writeStudentToofiComposerDraft(promptDraft);
  }, [promptDraft]);

  useEffect(() => {
    if (surfaceSupported) return;
    setOpen(false);
  }, [surfaceSupported]);

  useEffect(() => {
    const handleVisibility = (event: Event) => {
      const detail = (event as CustomEvent<{ open?: boolean }>).detail;
      setReaderSurfaceOpen(Boolean(detail?.open));
    };
    window.addEventListener(READER_TOOFI_VISIBILITY_EVENT, handleVisibility as EventListener);
    return () => window.removeEventListener(READER_TOOFI_VISIBILITY_EVENT, handleVisibility as EventListener);
  }, []);

  const effectiveSurfaceContext = useMemo<StudentToofiSurfaceContext>(() => {
    const intent = pendingIntent;
    const activeResourceId =
      activeThread && activeThread.resourceId !== "__workspace__" ? activeThread.resourceId : null;
    return {
      ...surfaceContext,
      linkedResourceId: intent?.linkedResourceId ?? surfaceContext.linkedResourceId ?? activeResourceId ?? null,
      linkedPlanId: intent?.linkedPlanId ?? surfaceContext.linkedPlanId ?? activeThread?.linkedPlanId ?? null,
    };
  }, [activeThread, pendingIntent, surfaceContext]);

  const currentProductMode = useMemo<ToofiProductMode | null>(
    () => pendingIntent?.productMode ?? activeThread?.currentProductMode ?? null,
    [activeThread?.currentProductMode, pendingIntent?.productMode],
  );

  const getRequestSurfaceContext = useCallback((): StudentToofiSurfaceContext => {
    const intent = pendingIntentRef.current;
    const activeResourceId =
      activeThread && activeThread.resourceId !== "__workspace__" ? activeThread.resourceId : null;
    return {
      ...surfaceContext,
      linkedResourceId: intent?.linkedResourceId ?? surfaceContext.linkedResourceId ?? activeResourceId ?? null,
      linkedPlanId: intent?.linkedPlanId ?? surfaceContext.linkedPlanId ?? activeThread?.linkedPlanId ?? null,
    };
  }, [activeThread, surfaceContext]);

  const setPromptDraft = useCallback((draft: string) => {
    setPromptDraftState(draft);
  }, []);

  const setCurrentProductMode = useCallback(
    (mode: ToofiProductMode | null) => {
      if (activeThreadId) {
        setPersistedState((current) => ({
          ...current,
          threads: current.threads.map((thread) =>
            thread.id === activeThreadId
              ? {
                  ...thread,
                  currentProductMode: mode,
                  updatedAt: Date.now(),
                }
              : thread,
          ),
          savedAt: Date.now(),
        }));
      }
      setPendingIntent((current) => {
        const next = normalizeStudentToofiOpenIntent({
          ...current,
          productMode: mode,
          modeSelectionSource: mode ? current?.modeSelectionSource ?? "manual" : null,
        });
        pendingIntentRef.current = next;
        return next;
      });
    },
    [activeThreadId],
  );

  const setSurfaceRegistrationSafe = useCallback((context: StudentToofiSurfaceRegistration | null) => {
    setSurfaceRegistration(context);
  }, []);

  const createFreshWorkspaceThread = useCallback(
    (intent?: StudentToofiOpenIntent | null) => {
      const normalized = normalizeStudentToofiOpenIntent(intent ?? pendingIntentRef.current);
      const requestContext = getRequestSurfaceContext();
      const thread = createStudentToofiThread({
        surfaceOrigin: surfaceContext.surface,
        linkedResourceId: normalized.linkedResourceId ?? requestContext.linkedResourceId ?? null,
        linkedPlanId: normalized.linkedPlanId ?? requestContext.linkedPlanId ?? null,
        productMode: normalized.productMode ?? currentProductMode,
      });
      thread.memoryMode = normalized.memoryMode === "temporary" ? "temporary" : "default";
      setPersistedState((current) =>
        normalizeThreadState({
          ...current,
          threads: [thread, ...current.threads],
          messagesByThread: {
            ...current.messagesByThread,
            [thread.id]: [],
          },
          threadSummariesByThread: {
            ...current.threadSummariesByThread,
            [thread.id]: createEmptyThreadSummary(thread.id),
          },
          savedAt: Date.now(),
        }),
      );
      setActiveThreadId(thread.id);
      setOpen(true);
      return thread;
    },
    [currentProductMode, getRequestSurfaceContext, surfaceContext.surface],
  );

  const startNewChat = useCallback(() => {
    const next = normalizeStudentToofiOpenIntent({
      open: true,
      prompt: "",
      entryPointId: null,
      threadId: null,
      linkedResourceId: surfaceContext.linkedResourceId ?? null,
      linkedPlanId: surfaceContext.linkedPlanId ?? null,
      productMode: currentProductMode,
      productIntent: null,
      modeSelectionSource: "manual",
      memoryMode: "default",
    });
    createFreshWorkspaceThread(next);
    pendingIntentRef.current = null;
    setPendingIntent(null);
    setPromptDraftState("");
  }, [createFreshWorkspaceThread, currentProductMode, surfaceContext.linkedPlanId, surfaceContext.linkedResourceId]);

  const startTemporaryChat = useCallback(() => {
    const next = normalizeStudentToofiOpenIntent({
      open: true,
      prompt: "",
      threadId: null,
      linkedResourceId: surfaceContext.linkedResourceId ?? null,
      linkedPlanId: surfaceContext.linkedPlanId ?? null,
      productMode: currentProductMode,
      productIntent: null,
      modeSelectionSource: "manual",
      entryPointId: "temporary_chat",
      memoryMode: "temporary",
    });
    createFreshWorkspaceThread(next);
    pendingIntentRef.current = null;
    setPendingIntent(null);
    setPromptDraftState("");
    captureToofiAnalytics("toofi_temporary_chat_started", {
      surface: surfaceContext.surface,
      linked_resource_id: surfaceContext.linkedResourceId ?? null,
      linked_plan_id: surfaceContext.linkedPlanId ?? null,
    });
  }, [createFreshWorkspaceThread, currentProductMode, surfaceContext.linkedPlanId, surfaceContext.linkedResourceId, surfaceContext.surface]);

  const backHome = useCallback(() => {
    setActiveThreadId(null);
  }, []);

  const switchThread = useCallback((threadId: string) => {
    setActiveThreadId(threadId);
    setOpen(true);
  }, []);

  const renameThread = useCallback((threadId: string, title: string) => {
    const trimmed = title.trim();
    if (!trimmed) return;
    setPersistedState((current) => ({
      ...current,
      threads: current.threads.map((thread) => (thread.id === threadId ? { ...thread, title: trimmed, updatedAt: Date.now() } : thread)),
      savedAt: Date.now(),
    }));
  }, []);

  const deleteThread = useCallback((threadId: string) => {
    setPersistedState((current) => {
      const nextMessagesByThread = { ...current.messagesByThread };
      const nextSummaries = { ...current.threadSummariesByThread };
      delete nextMessagesByThread[threadId];
      delete nextSummaries[threadId];
      return {
        ...current,
        threads: current.threads.filter((thread) => thread.id !== threadId),
        messagesByThread: nextMessagesByThread,
        threadSummariesByThread: nextSummaries,
        savedAt: Date.now(),
      };
    });
    setActiveThreadId((current) => (current === threadId ? null : current));
  }, []);

  const pinThread = useCallback((threadId: string) => {
    setPersistedState((current) =>
      normalizeThreadState({
        ...current,
        threads: current.threads.map((thread) =>
          thread.id === threadId ? { ...thread, pinned: true, updatedAt: Date.now() } : thread,
        ),
        savedAt: Date.now(),
      }),
    );
    captureToofiAnalytics("toofi_thread_pinned", { thread_id: threadId, surface: surfaceContext.surface });
  }, [surfaceContext.surface]);

  const unpinThread = useCallback((threadId: string) => {
    setPersistedState((current) =>
      normalizeThreadState({
        ...current,
        threads: current.threads.map((thread) =>
          thread.id === threadId ? { ...thread, pinned: false, updatedAt: Date.now() } : thread,
        ),
        savedAt: Date.now(),
      }),
    );
  }, []);

  const archiveThread = useCallback((threadId: string) => {
    setPersistedState((current) =>
      normalizeThreadState({
        ...current,
        threads: current.threads.map((thread) =>
          thread.id === threadId
            ? { ...thread, archived: true, archivedAt: Date.now(), updatedAt: Date.now() }
            : thread,
        ),
        savedAt: Date.now(),
      }),
    );
    setActiveThreadId((current) => (current === threadId ? null : current));
    captureToofiAnalytics("toofi_thread_archived", { thread_id: threadId, surface: surfaceContext.surface });
  }, [surfaceContext.surface]);

  const restoreThread = useCallback((threadId: string) => {
    setPersistedState((current) =>
      normalizeThreadState({
        ...current,
        threads: current.threads.map((thread) =>
          thread.id === threadId
            ? { ...thread, archived: false, archivedAt: null, updatedAt: Date.now() }
            : thread,
        ),
        savedAt: Date.now(),
      }),
    );
  }, []);

  const setThreadMemoryMode = useCallback((threadId: string, mode: ToofiMemoryMode) => {
    setPersistedState((current) =>
      normalizeThreadState({
        ...current,
        threads: current.threads.map((thread) =>
          thread.id === threadId ? { ...thread, memoryMode: mode, updatedAt: Date.now() } : thread,
        ),
        savedAt: Date.now(),
      }),
    );
    setPendingIntent((current) => {
      if (activeThreadId !== threadId) return current;
      const next = normalizeStudentToofiOpenIntent({
        ...current,
        memoryMode: mode,
      });
      pendingIntentRef.current = next;
      return next;
    });
  }, [activeThreadId]);

  const startSimilarChat = useCallback((threadId: string) => {
    const sourceThread = persistedStateRef.current.threads.find((thread) => thread.id === threadId);
    if (!sourceThread) return;
    const sourceSummary =
      persistedStateRef.current.threadSummariesByThread[threadId] ?? createEmptyThreadSummary(threadId);
    const cloned = {
      ...createStudentToofiThread({
        surfaceOrigin: sourceThread.surfaceOrigin ?? surfaceContext.surface,
        linkedResourceId: sourceThread.resourceId !== "__workspace__" ? sourceThread.resourceId : null,
        linkedPlanId: sourceThread.linkedPlanId ?? null,
        productMode: sourceThread.currentProductMode ?? currentProductMode,
      }),
      title: sourceThread.title,
      memoryMode: sourceThread.memoryMode ?? "default",
      branchSourceThreadId: sourceThread.id,
    };

    setPersistedState((current) =>
      normalizeThreadState({
        ...current,
        threads: [cloned, ...current.threads],
        messagesByThread: {
          ...current.messagesByThread,
          [cloned.id]: [],
        },
        threadSummariesByThread: {
          ...current.threadSummariesByThread,
          [cloned.id]: {
            ...createEmptyThreadSummary(cloned.id),
            rollingSummary: sourceSummary.rollingSummary,
            activeGoals: [...sourceSummary.activeGoals],
            openLoops: [...sourceSummary.openLoops],
          },
        },
        savedAt: Date.now(),
      }),
    );
    setActiveThreadId(cloned.id);
    setOpen(true);
  }, [currentProductMode, surfaceContext.surface]);

  const findMessageById = useCallback(
    (threadId: string, messageId: string) => (persistedState.messagesByThread[threadId] ?? []).find((message) => message.id === messageId) ?? null,
    [persistedState.messagesByThread],
  );

  const findBlockById = useCallback(
    (threadId: string, blockId: string) => {
      const threadMessages = persistedState.messagesByThread[threadId] ?? [];
      for (const message of threadMessages) {
        for (const block of message.blocks) {
          if ("id" in block && block.id === blockId) {
            return { message, block };
          }
        }
      }
      return null;
    },
    [persistedState.messagesByThread],
  );

  const updateBlockById = useCallback((threadId: string, blockId: string, transform: (message: ReaderAiMessage, block: ReaderAiBlock) => ReaderAiBlock) => {
    setPersistedState((current) =>
      updateThreadMessages(current, threadId, (threadMessages) =>
        threadMessages.map((message) => ({
          ...message,
          blocks: message.blocks.map((block) => ("id" in block && block.id === blockId ? transform(message, block) : block)),
        })),
      ),
    );
  }, []);

  const replaceThreadMessages = useCallback(
    (
      state: ReaderAiPersistedState,
      threadId: string,
      nextMessages: ReaderAiMessage[],
      summaryOverride?: ReturnType<typeof summarizeThreadMessages>,
    ): ReaderAiPersistedState => {
      const updatedAt = Date.now();
      const nextSummary =
        summaryOverride ??
        summarizeThreadMessages(
          threadId,
          nextMessages,
          state.threadSummariesByThread[threadId] ?? createEmptyThreadSummary(threadId),
        );
      return normalizeThreadState({
        ...state,
        threads: state.threads.map((thread) =>
          thread.id === threadId
            ? {
                ...thread,
                updatedAt,
                messageCount: nextMessages.length,
              }
            : thread,
        ),
        messagesByThread: {
          ...state.messagesByThread,
          [threadId]: nextMessages,
        },
        threadSummariesByThread: {
          ...state.threadSummariesByThread,
          [threadId]: nextSummary,
        },
        savedAt: updatedAt,
      });
    },
    [],
  );

  const maybeAutoTitleThread = useCallback((threadId: string, prompt: string) => {
    setPersistedState((current) => ({
      ...current,
      threads: current.threads.map((thread) => {
        if (thread.id !== threadId || !isAutoReaderAiThreadTitle(thread)) return thread;
        return {
          ...thread,
          title: deriveReaderAiThreadTitle({
            prompt,
            topicLabel: thread.lastTopicLabel ?? null,
          }),
          updatedAt: Date.now(),
        };
      }),
      savedAt: Date.now(),
    }));
  }, []);

  const ensureThread = useCallback(
    (intent?: StudentToofiOpenIntent | null) => {
      const normalized = normalizeStudentToofiOpenIntent(intent ?? pendingIntentRef.current);
      const shouldForceNewThread = Boolean(
        normalized.prompt && normalized.entryPointId && normalized.modeSelectionSource === "seeded",
      );
      if (activeThreadId && !shouldForceNewThread) return activeThreadId;
      return createFreshWorkspaceThread(normalized).id;
    },
    [activeThreadId, createFreshWorkspaceThread],
  );

  const runThreadPrompt = useCallback(
    async ({
      prompt,
      threadId,
      threadMessagesBefore,
      summaryBefore,
      requestContext,
      resolvedIntent,
      appendUser,
      clearDraft,
      autoTitle,
      guidedLessonAction,
    }: {
      prompt: string;
      threadId: string;
      threadMessagesBefore: ReaderAiMessage[];
      summaryBefore: ReturnType<typeof createEmptyThreadSummary>;
      requestContext: StudentToofiSurfaceContext;
      resolvedIntent: StudentToofiOpenIntent;
      appendUser: boolean;
      clearDraft: boolean;
      autoTitle: boolean;
      guidedLessonAction?: ReaderAiChatRequest["guidedLessonAction"];
    }) => {
      const trimmed = prompt.trim();
      if (!trimmed) return;
      const language = getStoredExplainLanguage() ?? detectPromptLanguage(trimmed);
      const resolvedProductMode = resolvedIntent.productMode ?? currentProductMode;
      const resolvedProductIntent =
        resolvedIntent.productIntent ?? inferToofiProductIntent({ mode: resolvedProductMode, prompt: trimmed });
      const resolvedSelectionSource =
        resolvedIntent.modeSelectionSource ??
        (resolvedIntent.productMode || resolvedIntent.productIntent || resolvedIntent.entryPointId ? "seeded" : "manual");
      const resolvedMemoryMode =
        resolvedIntent.memoryMode ??
        persistedStateRef.current.threads.find((thread) => thread.id === threadId)?.memoryMode ??
        "default";
      const userMessage = {
        ...createUserMessage(threadId, trimmed),
        actionType: "general_chat" as const,
        contextLabel: requestContext.title,
        usedContextScope: "general_scope" as const,
        productMode: resolvedProductMode,
        productIntent: resolvedProductIntent,
      };
      const ackMessage = {
        ...createSystemMessage(threadId, "Thinking through your study question..."),
        actionType: "general_chat" as const,
      };

      setPersistedState((current) => {
        const withMode = {
          ...current,
          threads: current.threads.map((thread) =>
            thread.id === threadId
              ? {
                  ...thread,
                  currentProductMode: resolvedProductMode,
                  memoryMode: resolvedMemoryMode,
                  updatedAt: Date.now(),
                }
              : thread,
          ),
          savedAt: Date.now(),
        };
        const withUser = appendUser ? appendMessageToThread(withMode, threadId, userMessage).state : withMode;
        const withAck = appendMessageToThread(withUser, threadId, ackMessage).state;
        return normalizeThreadState(withAck);
      });
      if (clearDraft) {
        setPromptDraftState("");
      }
      setOpen(true);
      if (autoTitle) {
        maybeAutoTitleThread(threadId, trimmed);
      }

      try {
        const response = await requestReaderAiChat({
          prompt: trimmed,
          threadId,
          resourceId: requestContext.linkedResourceId ?? undefined,
          linkedResourceId: requestContext.linkedResourceId ?? undefined,
          linkedPlanId: requestContext.linkedPlanId ?? undefined,
          surface: requestContext.surface,
          surfaceContext: requestContext,
          workspaceScope: "student_workspace",
          language,
          mode: "general_chat",
          productMode: resolvedProductMode,
          productIntent: resolvedProductIntent,
          memoryMode: resolvedMemoryMode,
          modeSelectionSource: resolvedSelectionSource,
          entryPointId: resolvedIntent.entryPointId ?? undefined,
          recentTurns: threadMessagesBefore
            .filter((message) => message.role !== "system")
            .slice(-20)
            .map((message) => ({
              role: message.role === "assistant" ? "assistant" as const : "user" as const,
              text: readerAiMessageToText(message)
                .trim()
                .slice(0, 420),
            }))
            .filter((turn) => turn.text),
          threadSummary: summaryBefore.rollingSummary,
          activeGoals: summaryBefore.activeGoals,
          openLoops: summaryBefore.openLoops,
          guidedLessonAction: guidedLessonAction ?? null,
        });

        captureToofiAnalytics("toofi_workspace_response_received", {
          surface: requestContext.surface,
          product_mode: response.productMode ?? resolvedProductMode,
          product_intent: response.productIntent ?? resolvedProductIntent,
          thread_id: threadId,
          linked_resource_id: requestContext.linkedResourceId ?? null,
          linked_plan_id: requestContext.linkedPlanId ?? null,
          source_origin: response.sourceOrigin,
          retry_used: response.runtime?.outputGovernor?.retryUsed ?? false,
          normalized: response.runtime?.outputGovernor?.normalized ?? false,
          fallback_used: response.runtime?.outputGovernor?.fallbackUsed ?? false,
          parse_source: response.runtime?.outputGovernor?.parseSource ?? null,
          quality_reasons: response.runtime?.outputGovernor?.qualityReasons ?? [],
          quality_severity: response.runtime?.outputGovernor?.qualitySeverity ?? null,
          premium_reasoning_used: response.runtime?.premiumReasoningUsed ?? false,
          premium_reasoning_attempted: response.runtime?.premiumReasoningAttempted ?? false,
          deep_review_generation: response.runtime?.deepReviewGeneration ?? false,
          plan_checkin_intent: response.runtime?.planCheckinIntent ?? false,
          exam_mode_enabled: response.runtime?.examModeEnabled ?? false,
          mastery_insights_enabled: response.runtime?.masteryInsightsEnabled ?? false,
          long_context_analysis_enabled: response.runtime?.longContextAnalysisEnabled ?? false,
          memory_mode: resolvedMemoryMode,
        });

        const assistantMessage = createAssistantMessage(threadId, response.blocks as ReaderAiBlock[], {
          actionType: "general_chat" as const,
          sourceOrigin: response.sourceOrigin,
          memoryUsage: response.memoryUsage,
          learningMode: response.learningMode,
          productMode: response.productMode ?? resolvedProductMode,
          productIntent: response.productIntent ?? resolvedProductIntent,
          runtimeMeta: response.runtime?.outputGovernor ?? response.telemetry?.outputGovernor ?? null,
        });

        setPersistedState((current) => {
          const withoutAck = updateThreadMessages(current, threadId, (threadMessages) =>
            threadMessages.filter((message) => !(message.role === "system" && message.actionType === "general_chat")),
          );
          const withAssistant = appendMessageToThread(withoutAck, threadId, assistantMessage).state;
          const nextSummary =
            response.nextThreadSummary
              ? coerceThreadSummary({
                  threadId,
                  rollingSummary: response.nextThreadSummary.rollingSummary,
                  activeGoals: response.nextThreadSummary.activeGoals,
                  openLoops: response.nextThreadSummary.openLoops,
                  lastUpdatedAt: Date.now(),
                  summaryVersion: (withoutAck.threadSummariesByThread[threadId]?.summaryVersion ?? 0) + 1,
                })
              : summarizeThreadMessages(
                  threadId,
                  withAssistant.messagesByThread[threadId] ?? [],
                  withoutAck.threadSummariesByThread[threadId],
                );
          return normalizeThreadState({
            ...withAssistant,
            threads: withAssistant.threads.map((thread) =>
              thread.id === threadId
                ? {
                    ...thread,
                    currentProductMode: response.productMode ?? resolvedProductMode,
                    memoryMode: resolvedMemoryMode,
                    updatedAt: Date.now(),
                  }
                : thread,
            ),
            threadSummariesByThread: {
              ...withAssistant.threadSummariesByThread,
              [threadId]: nextSummary,
            },
            savedAt: Date.now(),
          });
        });
        const planCheckinBlock = response.blocks.find(
          (block): block is Extract<ReaderAiBlock, { type: "plan_checkin" }> => block.type === "plan_checkin",
        );
        if (planCheckinBlock && profile?.id) {
          void (async () => {
            const activePlan = requestContext.linkedPlanId
              ? null
              : await fetchActiveStudyPlan(profile.id).catch(() => null);
            const planId = requestContext.linkedPlanId ?? activePlan?.id ?? null;
            if (!planId) return;
            await saveStudyPlanCheckin({
              planId,
              summary: planCheckinBlock.summary,
              checkinType:
                planCheckinBlock.status === "behind" || planCheckinBlock.status === "at_risk"
                  ? "replan"
                  : "daily",
              status: planCheckinBlock.status ?? "on_track",
              nextAction: planCheckinBlock.nextAction ?? null,
            }).catch(() => null);
            await queryClient.invalidateQueries({ queryKey: studyPlanQueryKeys.checkins(profile.id, planId) });
          })();
        }
      } catch (errorValue) {
        const errorText = errorValue instanceof Error ? errorValue.message : "Failed to reach Toofi right now.";
        const feedbackKind = isQuotaPauseMessage(errorText) ? "hint" : "error";
        setPersistedState((current) => {
          const withoutAck = updateThreadMessages(current, threadId, (threadMessages) =>
            threadMessages.filter((message) => !(message.role === "system" && message.actionType === "general_chat")),
          );
          return appendMessageToThread(
            withoutAck,
            threadId,
            createAssistantMessage(threadId, [
              {
                type: "feedback",
                kind: feedbackKind,
                text: errorText,
              },
            ]),
          ).state;
        });
      } finally {
        setPendingIntent(null);
        pendingIntentRef.current = null;
      }
    },
    [currentProductMode, maybeAutoTitleThread, profile?.id, queryClient],
  );

  const submitPrompt = useCallback(
    async (prompt: string, intentOverride?: StudentToofiOpenIntent | null) => {
      if (!available || !surfaceSupported) return;
      const trimmed = prompt.trim();
      if (!trimmed) return;
      if (intentOverride) {
        const normalizedOverride = normalizeStudentToofiOpenIntent(intentOverride);
        pendingIntentRef.current = normalizedOverride;
        setPendingIntent(normalizedOverride);
      }
      const resolvedIntent = normalizeStudentToofiOpenIntent(intentOverride ?? pendingIntentRef.current);
      const threadId = ensureThread(intentOverride);
      const stateSnapshot = persistedStateRef.current;
      const threadMessagesBefore = stateSnapshot.messagesByThread[threadId] ?? [];
      const summaryBefore = stateSnapshot.threadSummariesByThread[threadId] ?? createEmptyThreadSummary(threadId);
      await runThreadPrompt({
        prompt: trimmed,
        threadId,
        threadMessagesBefore,
        summaryBefore,
        requestContext: getRequestSurfaceContext(),
        resolvedIntent,
        appendUser: true,
        clearDraft: true,
        autoTitle: true,
        guidedLessonAction: null,
      });
    },
    [available, ensureThread, getRequestSurfaceContext, runThreadPrompt, surfaceSupported],
  );

  const startChatWithPrompt = useCallback(
    async (prompt: string, intentOverride?: StudentToofiOpenIntent | null) => {
      if (!available) return;
      await submitPrompt(prompt, intentOverride ?? pendingIntentRef.current);
    },
    [available, submitPrompt],
  );

  const openToofi = useCallback(
    (intent?: StudentToofiOpenIntent) => {
      if (!available || !surfaceSupported) return;
      const normalized = normalizeStudentToofiOpenIntent(intent);
      pendingIntentRef.current = normalized;
      setPendingIntent(normalized);
      if (normalized.threadId) {
        setActiveThreadId(normalized.threadId);
      }
      setOpen(normalized.open ?? true);
      if (normalized.prompt) {
        void startChatWithPrompt(normalized.prompt, normalized);
      }
    },
    [available, startChatWithPrompt, surfaceSupported],
  );

  const closeToofi = useCallback(() => {
    setOpen(false);
  }, []);

  const editMessage = useCallback(
    async (messageId: string, text: string) => {
      if (!activeThreadId) return;
      const trimmed = text.trim();
      if (!trimmed) return;
      const stateSnapshot = persistedStateRef.current;
      const threadMessages = stateSnapshot.messagesByThread[activeThreadId] ?? [];
      const targetIndex = threadMessages.findIndex((message) => message.id === messageId && message.role === "user");
      if (targetIndex < 0) return;
      const nextMessages = threadMessages
        .slice(0, targetIndex + 1)
        .map((message, index) =>
          index === targetIndex
            ? {
                ...message,
                blocks: message.blocks.map((block) =>
                  block.type === "markdown" ? { ...block, markdown: trimmed } : block,
                ),
              }
            : message,
        );
      const summaryBefore = summarizeThreadMessages(
        activeThreadId,
        nextMessages,
        stateSnapshot.threadSummariesByThread[activeThreadId] ?? createEmptyThreadSummary(activeThreadId),
      );
      setPersistedState((current) => replaceThreadMessages(current, activeThreadId, nextMessages, summaryBefore));

      const editedMessage = nextMessages[targetIndex];
      if (targetIndex === 0) {
        maybeAutoTitleThread(activeThreadId, trimmed);
      }
      captureToofiAnalytics("toofi_edit_message_regenerated", {
        threadId: activeThreadId,
        surface: surfaceContext.surface,
        messageIndex: targetIndex,
      });

      await runThreadPrompt({
        prompt: trimmed,
        threadId: activeThreadId,
        threadMessagesBefore: nextMessages,
        summaryBefore,
        requestContext: getRequestSurfaceContext(),
        resolvedIntent: normalizeStudentToofiOpenIntent({
          productMode: editedMessage.productMode ?? activeThread?.currentProductMode ?? currentProductMode,
          productIntent: editedMessage.productIntent ?? null,
          modeSelectionSource: "manual",
        }),
        appendUser: false,
        clearDraft: false,
        autoTitle: false,
        guidedLessonAction: null,
      });
    },
    [activeThread?.currentProductMode, activeThreadId, currentProductMode, getRequestSurfaceContext, maybeAutoTitleThread, replaceThreadMessages, runThreadPrompt, surfaceContext.surface],
  );

  const deleteMessage = useCallback(
    (messageId: string) => {
      if (!activeThreadId) return;
      const stateSnapshot = persistedStateRef.current;
      const threadMessages = stateSnapshot.messagesByThread[activeThreadId] ?? [];
      const targetIndex = threadMessages.findIndex((message) => message.id === messageId);
      if (targetIndex < 0) return;
      const nextMessages = threadMessages.slice(0, targetIndex);
      captureToofiAnalytics("toofi_delete_message_cluster_truncated", {
        threadId: activeThreadId,
        surface: surfaceContext.surface,
        removedCount: threadMessages.length - nextMessages.length,
      });
      setPersistedState((current) => replaceThreadMessages(current, activeThreadId, nextMessages));
    },
    [activeThreadId, replaceThreadMessages, surfaceContext.surface],
  );

  const submitMcq = useCallback(
    (
      blockId: string,
      selectedIndex: number,
      question?: {
        id: string;
        prompt: string;
        options: { id: string; label: string }[];
        correctIndex?: number | null;
        attemptId?: string | null;
        multi?: boolean;
      },
    ) => {
      if (!activeThreadId) return;
      const found = findBlockById(activeThreadId, blockId);
      const block = found?.block;
      if (!block || block.type !== "mcq") return;
      const activeQuestion =
        question ??
        (block.prompt && block.options
          ? {
              id: block.id,
              prompt: block.prompt,
              options: block.options,
              correctIndex: block.correctIndex,
              attemptId: block.attemptId,
              multi: block.multi,
            }
          : null);
      if (!activeQuestion) return;
      const selectedLabel = activeQuestion.options[selectedIndex]?.label?.trim() ?? `option ${selectedIndex + 1}`;
      const correctLabel =
        typeof activeQuestion.correctIndex === "number"
          ? activeQuestion.options[activeQuestion.correctIndex]?.label?.trim() ?? `option ${activeQuestion.correctIndex + 1}`
          : null;
      const feedback =
        typeof activeQuestion.correctIndex === "number"
          ? selectedIndex === activeQuestion.correctIndex
            ? `Correct. You picked "${selectedLabel}". Keep going, and make sure you can explain why the other options are weaker.`
            : correctLabel
              ? `Not quite. You picked "${selectedLabel}", but the better answer is "${correctLabel}" because it matches the prompt more directly, not just a related idea.`
              : `Not quite. The best answer is option ${activeQuestion.correctIndex + 1}.`
          : "Answer saved. Ask Toofi to explain why, then continue to the next question.";

      setPersistedState((current) => {
        const withUser = appendMessageToThread(
          current,
          activeThreadId,
          {
            ...createUserMessage(activeThreadId, `My answer: option ${selectedIndex + 1} for "${activeQuestion.prompt}"`),
            actionType: "quiz_answer",
          },
        ).state;
        return appendMessageToThread(
          withUser,
          activeThreadId,
          createAssistantMessage(activeThreadId, [{ type: "feedback", kind: "hint", text: feedback }]),
        ).state;
      });
      if (question) {
        captureToofiAnalytics("toofi_grouped_mcq_answered", {
          threadId: activeThreadId,
          blockId,
          questionId: question.id,
          selectedIndex,
        });
      }
    },
    [activeThreadId, findBlockById],
  );

  const submitGuidedLessonAnswer = useCallback(
    async ({ lessonId, stepIndex, answer, checkKind }: GuidedLessonSubmitInput) => {
      if (!activeThreadId || !answer.trim()) return;
      const stateSnapshot = persistedStateRef.current;
      const threadMessagesBefore = stateSnapshot.messagesByThread[activeThreadId] ?? [];
      const summaryBefore = stateSnapshot.threadSummariesByThread[activeThreadId] ?? createEmptyThreadSummary(activeThreadId);
      await runThreadPrompt({
        prompt: answer.trim(),
        threadId: activeThreadId,
        threadMessagesBefore,
        summaryBefore,
        requestContext: getRequestSurfaceContext(),
        resolvedIntent: normalizeStudentToofiOpenIntent({
          productMode: "learn",
          productIntent: "step_by_step",
          modeSelectionSource: "manual",
          entryPointId: "guided_lesson_answer",
        }),
        appendUser: true,
        clearDraft: false,
        autoTitle: false,
        guidedLessonAction: {
          type: "answer",
          lessonId,
          stepIndex,
          answer: answer.trim(),
          checkKind,
        },
      });
      captureToofiAnalytics("toofi_guided_lesson_answered", {
        lessonId,
        stepIndex,
        threadId: activeThreadId,
        surface: surfaceContext.surface,
      });
    },
    [activeThreadId, getRequestSurfaceContext, runThreadPrompt, surfaceContext.surface],
  );

  const continueGuidedLesson = useCallback(
    async ({ lessonId, stepIndex }: GuidedLessonControlInput) => {
      if (!activeThreadId) return;
      const stateSnapshot = persistedStateRef.current;
      const threadMessagesBefore = stateSnapshot.messagesByThread[activeThreadId] ?? [];
      const summaryBefore = stateSnapshot.threadSummariesByThread[activeThreadId] ?? createEmptyThreadSummary(activeThreadId);
      await runThreadPrompt({
        prompt: "Continue guided lesson.",
        threadId: activeThreadId,
        threadMessagesBefore,
        summaryBefore,
        requestContext: getRequestSurfaceContext(),
        resolvedIntent: normalizeStudentToofiOpenIntent({
          productMode: "learn",
          productIntent: "step_by_step",
          modeSelectionSource: "manual",
          entryPointId: "guided_lesson_continue",
        }),
        appendUser: false,
        clearDraft: false,
        autoTitle: false,
        guidedLessonAction: {
          type: "continue",
          lessonId,
          stepIndex,
        },
      });
      captureToofiAnalytics("toofi_guided_lesson_continued", {
        lessonId,
        stepIndex,
        threadId: activeThreadId,
        surface: surfaceContext.surface,
      });
    },
    [activeThreadId, getRequestSurfaceContext, runThreadPrompt, surfaceContext.surface],
  );

  const skipGuidedLessonStep = useCallback(
    async ({ lessonId, stepIndex }: GuidedLessonControlInput) => {
      if (!activeThreadId) return;
      const stateSnapshot = persistedStateRef.current;
      const threadMessagesBefore = stateSnapshot.messagesByThread[activeThreadId] ?? [];
      const summaryBefore = stateSnapshot.threadSummariesByThread[activeThreadId] ?? createEmptyThreadSummary(activeThreadId);
      await runThreadPrompt({
        prompt: "Skip this step and continue.",
        threadId: activeThreadId,
        threadMessagesBefore,
        summaryBefore,
        requestContext: getRequestSurfaceContext(),
        resolvedIntent: normalizeStudentToofiOpenIntent({
          productMode: "learn",
          productIntent: "step_by_step",
          modeSelectionSource: "manual",
          entryPointId: "guided_lesson_skip",
        }),
        appendUser: false,
        clearDraft: false,
        autoTitle: false,
        guidedLessonAction: {
          type: "skip",
          lessonId,
          stepIndex,
        },
      });
      captureToofiAnalytics("toofi_guided_lesson_skipped", {
        lessonId,
        stepIndex,
        threadId: activeThreadId,
        surface: surfaceContext.surface,
      });
    },
    [activeThreadId, getRequestSurfaceContext, runThreadPrompt, surfaceContext.surface],
  );

  const startEssay = useCallback(
    (blockId: string, prompt: string, _minWords?: number, promptId?: string) => {
      if (!activeThreadId) return;
      if (promptId) {
        captureToofiAnalytics("toofi_grouped_essay_prompt_started", {
          threadId: activeThreadId,
          blockId,
          promptId,
        });
        setPersistedState((current) =>
          appendMessageToThread(
            current,
            activeThreadId,
            createAssistantMessage(activeThreadId, [
              {
                type: "essay_workspace",
                id: `essay-workspace-${promptId}-${Date.now()}`,
                prompt,
                draft: "",
                wordCount: 0,
              },
            ]),
          ).state,
        );
        return;
      }
      updateBlockById(activeThreadId, blockId, (_message, block) =>
        block.type === "essay_prompt"
          ? {
              type: "essay_workspace",
              id: block.id,
              prompt,
              draft: "",
              wordCount: 0,
            }
          : block,
      );
    },
    [activeThreadId, updateBlockById],
  );

  const changeEssayDraft = useCallback(
    (blockId: string, draft: string) => {
      if (!activeThreadId) return;
      updateBlockById(activeThreadId, blockId, (_message, block) =>
        block.type === "essay_workspace"
          ? {
              ...block,
              draft,
              savedAt: Date.now(),
              wordCount: draft.trim().split(/\s+/).filter(Boolean).length,
            }
          : block,
      );
    },
    [activeThreadId, updateBlockById],
  );

  const submitEssay = useCallback(
    async (blockId: string, prompt: string, draft: string) => {
      if (!activeThreadId || !draft.trim()) return;
      const linkedResourceId = getRequestSurfaceContext().linkedResourceId;
      if (!linkedResourceId) {
        setPersistedState((current) =>
          appendMessageToThread(
            current,
            activeThreadId,
            createAssistantMessage(activeThreadId, [
              {
                type: "feedback",
                kind: "hint",
                text: "Essay evaluation works best when Toofi is linked to a specific resource. Open a resource or ask for feedback in normal chat.",
              },
            ]),
          ).state,
        );
        return;
      }

      setPersistedState((current) => {
        const withUser = appendMessageToThread(current, activeThreadId, {
          ...createUserMessage(activeThreadId, "Submit essay draft for evaluation."),
          actionType: "essay_evaluate",
        }).state;
        return appendMessageToThread(
          withUser,
          activeThreadId,
          {
            ...createSystemMessage(activeThreadId, "Preparing writing feedback..."),
            actionType: "essay_evaluate",
          },
        ).state;
      });

      try {
        const result = await requestAiAction({
          action: "evaluate_essay",
          term: prompt,
          context: prompt,
          resourceId: linkedResourceId,
          pageNumber: 1,
          studentAnswer: draft,
        });
        if (!result.evaluation) {
          throw new Error("No evaluation returned.");
        }
        setPersistedState((current) => {
          const withoutAck = updateThreadMessages(current, activeThreadId, (threadMessages) =>
            threadMessages.filter((message) => !(message.role === "system" && message.actionType === "essay_evaluate")),
          );
          return appendMessageToThread(
            withoutAck,
            activeThreadId,
            createAssistantMessage(activeThreadId, [
              {
                type: "essay_evaluation",
                score: result.evaluation.score,
                highlights: result.evaluation.highlights,
                missing: result.evaluation.missing,
                modelAnswer: result.evaluation.modelAnswer,
              },
            ]),
          ).state;
        });
      } catch (errorValue) {
        setPersistedState((current) => {
          const withoutAck = updateThreadMessages(current, activeThreadId, (threadMessages) =>
            threadMessages.filter((message) => !(message.role === "system" && message.actionType === "essay_evaluate")),
          );
          return appendMessageToThread(
            withoutAck,
            activeThreadId,
            createAssistantMessage(activeThreadId, [
              {
                type: "feedback",
                kind: "error",
                text: errorValue instanceof Error ? errorValue.message : "Failed to evaluate essay.",
              },
            ]),
          ).state;
        });
      }
    },
    [activeThreadId, getRequestSurfaceContext],
  );

  const savePlan = useCallback(
    async (messageId: string) => {
      if (!activeThreadId) return;
      const message = findMessageById(activeThreadId, messageId);
      if (!message) return;
      const payload = buildSaveStudyPlanPayloadFromMessage({
        threadId: activeThreadId,
        message,
        linkedResourceId: getRequestSurfaceContext().linkedResourceId ?? null,
        savedFrom: "toofi_workspace",
      });
      if (!payload) return;

      try {
        const response = await requestSaveStudyPlan(payload);
        setPersistedState((current) => {
          const updated = updateThreadMessages(current, activeThreadId, (threadMessages) =>
            threadMessages.map((entry) =>
              entry.id !== messageId
                ? entry
                : {
                    ...entry,
                    blocks: entry.blocks.map((block) =>
                      block.type === "plan_overview"
                        ? {
                            ...block,
                            confirmationRequired: false,
                            savedPlanId: response.plan.id,
                            status: response.plan.status,
                          }
                        : block,
                    ),
                  },
            ),
          );
          const withFeedback = appendMessageToThread(
            updated,
            activeThreadId,
            createAssistantMessage(activeThreadId, [
              {
                type: "feedback",
                kind: "success",
                text: "Saved as your active study plan. Your dashboard will now follow it.",
              },
              {
                type: "plan_checkin",
                title: "Plan saved",
                summary: "This plan is now live across your dashboard and Toofi workspace.",
                status: "on_track",
                nextAction: "Use today’s plan on the dashboard or ask Toofi to refine it.",
              },
            ]),
          ).state;
          return withFeedback;
        });
        toast.success("Saved as your active study plan.");
        await Promise.all([
          queryClient.invalidateQueries({ queryKey: studyPlanQueryKeys.active(profile?.id) }),
          queryClient.invalidateQueries({ queryKey: studyPlanQueryKeys.today(profile?.id) }),
          queryClient.invalidateQueries({ queryKey: studyPlanQueryKeys.drafts(profile?.id) }),
        ]);
      } catch (errorValue) {
        setPersistedState((current) =>
          appendMessageToThread(
            current,
            activeThreadId,
            createAssistantMessage(activeThreadId, [
              {
                type: "feedback",
                kind: "error",
                text: errorValue instanceof Error ? errorValue.message : "Failed to save this study plan.",
              },
            ]),
          ).state,
        );
      }
    },
    [activeThreadId, findMessageById, getRequestSurfaceContext, profile?.id, queryClient],
  );

  const refinePlan = useCallback(
    (messageId: string, blockId: string) => {
      if (!activeThreadId) return;
      const message = findMessageById(activeThreadId, messageId);
      const planOverview = message?.blocks.find((block): block is Extract<ReaderAiBlock, { type: "plan_overview" }> => block.type === "plan_overview" && block.id === blockId);
      if (!planOverview) return;
      setPromptDraftState(`Refine this study plan for me: ${planOverview.title}. I want to change the pacing, milestones, or today's tasks.`);
      toast.message("Tell Toofi what you want changed, then send.");
    },
    [activeThreadId, findMessageById],
  );

  const promptWarning = useMemo(() => {
    if (!activeThreadId) return "none" as const;
    const turnCount = (persistedState.messagesByThread[activeThreadId] ?? []).filter((message) => message.role !== "system").length;
    if (turnCount >= 40) return "strong" as const;
    if (turnCount >= 32) return "soft" as const;
    return "none" as const;
  }, [activeThreadId, persistedState.messagesByThread]);

  const value = useMemo<StudentToofiProviderValue>(
    () => ({
      available: available && surfaceSupported,
      open,
      activeThreadId,
      threads: persistedState.threads.filter((thread) => thread.scope === "workspace"),
      messages,
      promptDraft,
      surfaceContext: effectiveSurfaceContext,
      promptWarning,
      currentProductMode,
      readerSurfaceOpen,
      openToofi,
      closeToofi,
      backHome,
      startNewChat,
      startTemporaryChat,
      switchThread,
      renameThread,
      deleteThread,
      pinThread,
      unpinThread,
      archiveThread,
      restoreThread,
      startSimilarChat,
      setThreadMemoryMode,
      startChatWithPrompt,
      submitPrompt,
      setCurrentProductMode,
      setPromptDraft,
      setSurfaceRegistration: setSurfaceRegistrationSafe,
      editMessage,
      deleteMessage,
      submitMcq,
      startEssay,
      changeEssayDraft,
      submitEssay,
      submitGuidedLessonAnswer,
      continueGuidedLesson,
      skipGuidedLessonStep,
      savePlan,
      refinePlan,
    }),
    [
      activeThreadId,
      available,
      backHome,
      changeEssayDraft,
      closeToofi,
      continueGuidedLesson,
      deleteMessage,
      deleteThread,
      editMessage,
      effectiveSurfaceContext,
      messages,
      currentProductMode,
      open,
      openToofi,
      persistedState.threads,
      promptDraft,
      promptWarning,
      readerSurfaceOpen,
      refinePlan,
      renameThread,
      savePlan,
      setPromptDraft,
      setSurfaceRegistrationSafe,
      startChatWithPrompt,
      startEssay,
      startNewChat,
      startTemporaryChat,
      submitGuidedLessonAnswer,
      submitEssay,
      submitMcq,
      submitPrompt,
      skipGuidedLessonStep,
      startSimilarChat,
      setCurrentProductMode,
      setThreadMemoryMode,
      surfaceSupported,
      switchThread,
      unpinThread,
      archiveThread,
      restoreThread,
      pinThread,
    ],
  );

  return <StudentToofiContext.Provider value={value}>{children}</StudentToofiContext.Provider>;
}
